import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tinykeys } from 'tinykeys';

import { useCommandPalette } from './CommandPalette';

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Global keyboard shortcuts.
 * - `Mod+K` / `Ctrl+K` — open command palette (works everywhere)
 * - `/` — focus global search (falls back to opening palette if no search exists)
 * - `g d`, `g p`, `g a` — navigate shortcuts (ignored while typing)
 * - `n` — new policy wizard (ignored while typing)
 * - `?` — open palette (help placeholder)
 * - `Esc` handling is owned by the palette itself.
 */
export function KeyboardShortcuts() {
  const navigate = useNavigate();
  const { setOpen, toggle } = useCommandPalette();

  useEffect(() => {
    const unsubscribe = tinykeys(window, {
      '$mod+k': (e) => {
        e.preventDefault();
        toggle();
      },
      '$mod+K': (e) => {
        e.preventDefault();
        toggle();
      },
      '/': (e) => {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        setOpen(true);
      },
      '?': (e) => {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        setOpen(true);
      },
      'g d': (e) => {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        navigate('/dashboard');
      },
      'g p': (e) => {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        navigate('/policies');
      },
      'g a': (e) => {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        navigate('/activities');
      },
      n: (e) => {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        navigate('/policies/wizard');
      },
      Escape: () => {
        setOpen(false);
      },
    });
    return unsubscribe;
  }, [navigate, setOpen, toggle]);

  return null;
}
